import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Play, Pause, Download, Heart, Share2, DollarSign, X } from "lucide-react";
import { api, resolveFileUrl, contentToQueue, API_BASE } from "../api";
import { usePlayer, useAuth } from "../contexts";
import { toast } from "sonner";
import ShareSheet from "../components/ShareSheet";
import { StreamLinks } from "../components/StreamLinks";
import { StarRating, formatCompact } from "../components/StarRating";

export default function MusicDetailPage({ ctype }) {
  const { id } = useParams();
  const { user } = useAuth();
  const [item, setItem] = useState(null);
  const [stats, setStats] = useState({ downloads: 0, plays: 0, rating_avg: 0, rating_count: 0, my_rating: null });
  const [shareOpen, setShareOpen] = useState(false);
  const [tipOpen, setTipOpen] = useState(false);
  const { playList, enqueue, current, isPlaying, toggle } = usePlayer();

  useEffect(() => {
    api.get(`/content/${ctype}/${id}`).then((r) => setItem(r.data));
    api.get(`/content/${ctype}/${id}/stats`).then((r) => setStats(r.data)).catch(() => {});
  }, [ctype, id]);

  const queue = useMemo(() => (item ? contentToQueue(item, ctype) : []), [item, ctype]);

  if (!item) {
    return <div className="max-w-[1600px] mx-auto p-10 font-mono-label text-neutral-500">Loading…</div>;
  }

  const hasAudio = queue.length > 0;
  const multiTrack = queue.length > 1;
  const canDownload = item.allow_downloads !== false && hasAudio;
  const isCurrentQueueTrack = (idx) => current && queue[idx] && current.id === queue[idx].id;
  // Ctype-aware wording — mixtapes vs singles vs videos vs books
  const releaseLabel = ({ mixtapes: "mixtape", singles: "single", videos: "video", books: "book" }[ctype]) || "release";
  const playLabel = multiTrack ? "Play All" : "Play";

  const submitRating = async (stars) => {
    if (!user) {
      toast.error("Sign in to rate this tape");
      return;
    }
    try {
      const r = await api.post(`/content/${ctype}/${id}/rate`, { stars });
      setStats((s) => ({ ...s, ...r.data }));
      toast.success(`Rated ${stars} star${stars > 1 ? "s" : ""}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save rating");
    }
  };

  const downloadHref = `${process.env.REACT_APP_BACKEND_URL}/api/content/${ctype}/${id}/download`;

  return (
    <div className="pb-32">
      <div className="relative grain-bg border-b border-neutral-800">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `url('${resolveFileUrl(item.cover_image)}')`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "blur(40px)",
          }}
        />
        <div className="relative max-w-[1600px] mx-auto px-4 sm:px-6 py-16 grid md:grid-cols-[420px_1fr] gap-10 items-start z-10">
          <div className="aspect-square border border-neutral-800 overflow-hidden">
            <img src={resolveFileUrl(item.cover_image)} alt={item.title} className="w-full h-full object-cover" data-testid="detail-cover" />
          </div>

          <div>
            <div className="font-mono-label text-[11px] text-[var(--accent)] mb-3">
              {ctype.toUpperCase()} / {item.genre} / {item.year}
              {multiTrack && <span className="ml-3 text-neutral-400">· {queue.length} TRACKS</span>}
            </div>

            <h1 className="font-display text-5xl md:text-7xl uppercase leading-none mb-4" data-testid="detail-title">
              {item.title}
            </h1>
            <div className="font-headline text-xl uppercase text-neutral-300 mb-6">
              by {item.artist}
            </div>

            {/* Stats row: downloads + rating */}
            <div className="flex items-center gap-6 flex-wrap mb-4" data-testid="detail-stats">
              <div className="flex items-baseline gap-2">
                <span className="font-display text-4xl leading-none" data-testid="detail-downloads-count">
                  {formatCompact(stats.downloads)}
                </span>
                <span className="font-mono-label text-xs text-neutral-400">Downloads</span>
              </div>
              <div className="flex items-center gap-3">
                <StarRating value={stats.rating_avg} count={stats.rating_count} size={20} data-testid="detail-avg-rating" />
              </div>
            </div>

            {/* Genre chip */}
            {item.genre && (
              <div className="mb-6">
                <span className="chip font-mono-label text-[11px] uppercase bg-neutral-900 border border-neutral-700 px-3 py-1.5">
                  {item.genre}
                </span>
              </div>
            )}

            <div className="w-full max-w-md h-px bg-neutral-800 mb-6" />

            {item.description && (
              <p className="text-neutral-400 max-w-2xl mb-6">{item.description}</p>
            )}

            {/* Primary CTAs */}
            <div className="grid grid-cols-2 gap-3 max-w-md mb-3">
              <button
                onClick={() => hasAudio && playList(queue, 0)}
                disabled={!hasAudio}
                className="flex items-center justify-center gap-2 py-4 bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white font-headline uppercase tracking-wider text-base disabled:opacity-40"
                data-testid="detail-play-btn"
              >
                <Play size={18} fill="currentColor" /> {playLabel}
              </button>
              <a
                href={canDownload ? downloadHref : undefined}
                onClick={(e) => { if (!canDownload) { e.preventDefault(); return; } setStats((s) => ({ ...s, downloads: (s.downloads || 0) + 1 })); }}
                aria-disabled={!canDownload}
                className={`flex items-center justify-center gap-2 py-4 border-2 font-headline uppercase tracking-wider text-base ${canDownload ? "border-neutral-700 hover:border-[var(--accent)] hover:text-[var(--accent)]" : "border-neutral-800 text-neutral-600 pointer-events-none"}`}
                data-testid="detail-download-btn"
              >
                <Download size={18} /> Download
              </a>
            </div>

            {/* Tip + Share row */}
            <div className="flex items-center gap-3 max-w-md">
              <button
                onClick={() => setTipOpen(true)}
                className="flex-1 flex items-center justify-center gap-2 py-3 text-white font-headline uppercase tracking-wider text-base tip-gradient hover:brightness-110 transition-all"
                data-testid="detail-tip-btn"
              >
                <Heart size={16} fill="currentColor" /> Tip This Artist
              </button>
              <button
                onClick={() => setShareOpen(true)}
                className="p-3 border border-neutral-700 hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors"
                data-testid="detail-share-btn"
                aria-label="Share this tape"
              >
                <Share2 size={18} />
              </button>
            </div>

            <StreamLinks links={item.stream_links} />

            {/* Rate this mixtape */}
            <div className="mt-8">
              <div className="font-mono-label text-xs text-neutral-300 mb-2">
                {stats.my_rating ? "Your rating:" : `Rate this ${releaseLabel}:`}
              </div>
              <StarRating
                value={stats.my_rating || 0}
                onChange={submitRating}
                interactive
                size={30}
                data-testid="detail-rate"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Playable tracklist — one row per track for multi-track mixtapes */}
      {multiTrack && (
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-12">
          <div className="font-mono-label text-[10px] text-[var(--accent)] mb-2">TRACKLIST</div>
          <h2 className="font-headline text-2xl uppercase mb-6">Inside the tape</h2>
          <ul className="border border-neutral-800" data-testid="detail-tracks">
            {queue.map((t, i) => {
              const active = isCurrentQueueTrack(i);
              return (
                <li
                  key={t.id}
                  className={`px-5 py-4 flex items-center justify-between border-b border-neutral-800 last:border-0 ${active ? "bg-neutral-950" : "hover:bg-neutral-950"}`}
                  data-testid={`detail-track-${i}`}
                >
                  <div className="flex items-center gap-4 min-w-0">
                    <button
                      onClick={() => (active ? toggle() : playList(queue, i))}
                      className="w-9 h-9 flex items-center justify-center border border-neutral-700 hover:border-[var(--accent)] hover:text-[var(--accent)]"
                      aria-label={active && isPlaying ? "Pause" : "Play"}
                      data-testid={`detail-track-play-${i}`}
                    >
                      {active && isPlaying ? <Pause size={14} /> : <Play size={14} />}
                    </button>
                    <span className="font-mono-label text-[10px] text-neutral-500 w-6">{(i + 1).toString().padStart(2, "0")}</span>
                    <span className={`font-headline text-base uppercase truncate ${active ? "text-[var(--accent)]" : ""}`}>{t.title}</span>
                  </div>
                  <span className="font-mono-label text-[10px] text-neutral-500 truncate ml-3">{item.artist}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Legacy tracklist fallback */}
      {!multiTrack && item.tracklist?.length > 0 && (
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-12">
          <div className="font-mono-label text-[10px] text-[var(--accent)] mb-2">TRACKLIST</div>
          <h2 className="font-headline text-2xl uppercase mb-6">Inside the tape</h2>
          <ul className="border border-neutral-800" data-testid="detail-tracklist">
            {item.tracklist.map((t, i) => (
              <li key={`${item.id}-${i}-${t}`} className="px-5 py-4 flex items-center justify-between border-b border-neutral-800 last:border-0 hover:bg-neutral-950">
                <div className="flex items-center gap-4">
                  <span className="font-mono-label text-[10px] text-neutral-500 w-6">{(i + 1).toString().padStart(2, "0")}</span>
                  <span className="font-headline text-base uppercase">{t}</span>
                </div>
                <span className="font-mono-label text-[10px] text-neutral-500">{item.artist}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <ShareSheet open={shareOpen} onClose={() => setShareOpen(false)} title={item.title} artist={item.artist} ctype={ctype} id={item.id} />
      {tipOpen && (
        <QuickTipModal
          artistName={item.artist}
          onClose={() => setTipOpen(false)}
          onSubmit={async (amount, message) => {
            try {
              // Resolve artist_id from either the item or a profile lookup by artist display_name
              let artistId = item.artist_id;
              if (!artistId) {
                const r = await api.get(`/artists`).catch(() => ({ data: [] }));
                const match = (r.data || []).find((a) => a.display_name === item.artist);
                artistId = match?.id;
              }
              if (!artistId) throw new Error("Artist not found");
              const r = await api.post("/payments/tip", {
                artist_id: artistId,
                amount: parseFloat(amount),
                message,
                origin_url: window.location.origin,
              });
              if (r.data?.url) window.location.href = r.data.url;
            } catch (e) {
              toast.error(e.response?.data?.detail || e.message || "Tip failed");
            }
          }}
        />
      )}
    </div>
  );
}

function QuickTipModal({ artistName, onClose, onSubmit }) {
  const [amount, setAmount] = useState(5);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await onSubmit(amount, msg); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[70] bg-black/85 flex items-start sm:items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="quick-tip-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-[var(--surface)] border border-neutral-800 p-6 max-w-md w-full my-8">
        <div className="flex items-center justify-between mb-2">
          <div className="font-mono-label text-[10px] text-[var(--accent)]">TIP THE ARTIST</div>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <h3 className="font-display text-3xl uppercase mb-6">Support {artistName}</h3>
        <div className="flex flex-wrap gap-2 mb-4">
          {[5, 10, 25, 50, 100].map((v) => (
            <button key={v} onClick={() => setAmount(v)} className={`chip ${amount===v ? "chip-accent" : ""}`} data-testid={`quick-tip-amt-${v}`}>${v}</button>
          ))}
        </div>
        <input type="number" min="1" step="1" value={amount} onChange={(e) => setAmount(Number(e.target.value) || 0)} className="input-brut mb-3" data-testid="quick-tip-amount" />
        <textarea placeholder="Optional message…" value={msg} onChange={(e) => setMsg(e.target.value)} rows={3} className="input-brut mb-4 resize-none" data-testid="quick-tip-message" />
        <div className="flex gap-2">
          <button onClick={submit} disabled={busy} className="btn-primary flex-1 justify-center" data-testid="quick-tip-submit">
            <DollarSign size={14} /> {busy ? "Redirecting…" : `Tip $${amount}`}
          </button>
          <button onClick={onClose} className="btn-ghost">Cancel</button>
        </div>
      </div>
    </div>
  );
}
